import { useEffect, useState } from 'react';
import {
  AI30_COUPON_CODE,
  AI30_LANDING_PATH,
  AI30_PENDING_COUPON_KEY,
} from '../utils/ai30Campaign';
import { trackInAppBrowserEvent } from '../utils/eventTracker';
import {
  buildExternalBrowserUrl,
  getInAppBrowserName,
  isAndroidUserAgent,
  isEmbeddedWebView,
  isIOSUserAgent,
  isInAppExemptPath,
  shouldAutoOpenExternalBrowser,
  withCouponQuery,
} from '../utils/inAppBrowser';
import './KakaoBrowserDetect.css';

const BANNER_DISMISS_KEY = 'inapp_banner_dismissed';
const REDIRECT_ATTEMPTED_KEY = 'inapp_redirect_attempted';

const openInSafari = (url) => {
  const urlWithoutScheme = url.replace(/^https?:\/\//, '');
  window.location.href = `x-safari-https://${urlWithoutScheme}`;
};

const resolveExternalUrl = () => {
  let pending = '';
  try {
    pending = (localStorage.getItem(AI30_PENDING_COUPON_KEY) || '').trim();
  } catch {
    pending = '';
  }
  const coupon = pending || (
    window.location.pathname.startsWith(AI30_LANDING_PATH) ? AI30_COUPON_CODE : ''
  );
  return withCouponQuery(window.location.href, coupon);
};

const KakaoBrowserDetect = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent;
    if (!isEmbeddedWebView(ua) || isInAppExemptPath(window.location.pathname)) return undefined;

    const browser = getInAppBrowserName(ua) || 'webview';
    let cancelled = false;
    const alreadyAttempted = sessionStorage.getItem(REDIRECT_ATTEMPTED_KEY) === 'true';
    const dismissed = sessionStorage.getItem(BANNER_DISMISS_KEY) === 'true';
    const currentUrl = resolveExternalUrl();
    const isAndroid = isAndroidUserAgent(ua);
    const isIOS = isIOSUserAgent(ua);

    if (shouldAutoOpenExternalBrowser(ua) && !alreadyAttempted) {
      sessionStorage.setItem(REDIRECT_ATTEMPTED_KEY, 'true');
      try {
        if (browser === 'kakao') {
          window.location.href = `kakaotalk://web/openExternal?url=${encodeURIComponent(currentUrl)}`;
          setTimeout(() => {
            if (isAndroid) window.location.href = buildExternalBrowserUrl(currentUrl, { userAgent: ua });
            else if (isIOS) openInSafari(currentUrl);
          }, 500);
        } else if (isAndroid) {
          window.location.href = buildExternalBrowserUrl(currentUrl, { userAgent: ua });
        } else if (isIOS) {
          openInSafari(currentUrl);
        } else {
          window.open(currentUrl, '_blank') || (window.location.href = currentUrl);
        }
      } catch (_e) {
        // 리다이렉트 실패해도 인앱 브라우저에서 사이트 정상 사용 가능
      }
    }

    const timer = setTimeout(() => {
      if (cancelled || dismissed) return;
      if (!isEmbeddedWebView(navigator.userAgent)) return;
      setShowBanner(true);
      trackInAppBrowserEvent('banner_shown', {
        browser,
        platform: isIOS ? 'ios' : (isAndroid ? 'android' : 'other'),
      });
    }, 1500);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, []);

  if (!showBanner) return null;

  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const isAndroid = isAndroidUserAgent(ua);
  const currentUrl = resolveExternalUrl();

  const handleOpenExternal = () => {
    trackInAppBrowserEvent('open_external_clicked', {
      browser: getInAppBrowserName(ua) || 'unknown',
      platform: isAndroid ? 'android' : (isIOSUserAgent(ua) ? 'ios' : 'other'),
      entry: 'banner',
    });
    if (isAndroid) {
      window.location.href = buildExternalBrowserUrl(currentUrl, { userAgent: ua });
      return;
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(currentUrl)
        .then(() => setCopied(true))
        .catch(() => {});
    }
  };

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(BANNER_DISMISS_KEY, 'true');
    } catch {
      // ignore
    }
    setShowBanner(false);
  };

  const message = copied
    ? '링크를 복사했어요. 사파리나 크롬에 붙여넣어 주세요'
    : '브라우저 앱에서 열면 로그인이 바로 돼요';

  return (
    <div className="inapp-browser-banner" role="status">
      <p>{message}</p>
      {!copied && (
        <button type="button" className="inapp-browser-banner__primary" onClick={handleOpenExternal}>
          {isAndroid ? '브라우저로 열기' : '링크 복사'}
        </button>
      )}
      <button
        type="button"
        aria-label="닫기"
        className="inapp-browser-banner__close"
        onClick={handleDismiss}
      >
        <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default KakaoBrowserDetect;
